import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";

import {
  PREFERS_DARK_QUERY,
  applyResolvedTheme,
  resolveTheme,
  systemPrefersDark
} from "./applyTheme";
import { readThemePreference, writeThemePreference } from "./storage";
import type { ResolvedTheme, ThemePreference } from "./types";

type ThemeContextValue = {
  /** The stored choice: light, dark or follow the OS. */
  preference: ThemePreference;
  /** What is painted right now. */
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

type Props = {
  children: ReactNode;
  /** Test hook: overrides the value read from localStorage. */
  initialPreference?: ThemePreference;
};

export function ThemeProvider({ children, initialPreference }: Props) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initialPreference ?? readThemePreference()
  );
  const [osDark, setOsDark] = useState<boolean>(systemPrefersDark);

  // Track the OS only while the user is on "system"; a fixed choice ignores it.
  useEffect(() => {
    if (preference !== "system") return undefined;
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return undefined;
    const media = window.matchMedia(PREFERS_DARK_QUERY);
    setOsDark(media.matches);
    const onChange = (event: MediaQueryListEvent) => setOsDark(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  const resolvedTheme = resolveTheme(preference, osDark);

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writeThemePreference(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme, setPreference]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

/**
 * For leaf components that may be mounted without the provider (tests render
 * `ChakraProvider`, and with it the toaster, on its own). Returns `null` there.
 */
export function useOptionalTheme(): ThemeContextValue | null {
  return useContext(ThemeContext);
}
